import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { usePlansStore } from '@/stores/plansStore';

const MIN_ROWS = 3;
const MAX_ROWS = 14;

/**
 * The tab's own way in (D1): a prompt, read as a request document (docs/plans/chat-on-document-pipeline.md). The
 * document's page follows it: what ZAC found, its questions with suggested answers, then the plan.
 */
export function NewPlanPrompt() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const createFromPrompt = usePlansStore((s) => s.createFromPrompt);
  const busy = usePlansStore((s) => s.busy.create ?? false);

  // The box grows with the text, up to a point; nobody scrolls inside a three-line box.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(Math.max(el.scrollHeight, line * MIN_ROWS), line * MAX_ROWS)}px`;
  }, [prompt]);

  const canSubmit = prompt.trim() !== '' && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    const id = await createFromPrompt(prompt.trim());
    if (id !== null) navigate(`/changes/documents/${id}`);
  };

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <label htmlFor="new-plan-prompt" className="text-sm font-medium text-foreground">What do you want to change?</label>
      <textarea
        id="new-plan-prompt"
        ref={textareaRef}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={onEnter}
        rows={MIN_ROWS}
        disabled={busy}
        placeholder='e.g. "Create a new phone queue called Support in site HQ with members Alice and Bob"'
        className="mt-2 w-full resize-none overflow-y-auto rounded-lg border border-border bg-background px-3 py-2 text-sm leading-5 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
      />

      <div className="mt-3 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {busy ? 'Starting to read your request…' : 'ZAC reads the request, asks what it needs to know, then makes the plan. Nothing changes until you approve it.'}
        </span>
        <button
          onClick={() => void submit()}
          disabled={!canSubmit}
          title="⌘↵ / Ctrl↵"
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? 'Starting…' : 'Read and plan'}
        </button>
      </div>
    </div>
  );
}
